import { Link, createFileRoute } from '@tanstack/react-router'
import { RfaCode } from '@backend/integrations/auth/bankid/types'
import { XIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import QRCode from 'react-qr-code'

import BankIDLogo from '@/assets/icons/BankIDLogo'
import { AuthSplitLayout } from '@/components/layout/auth-split-layout'
import { ProgressBorder } from '@/components/organisms/progress-border'
import { Button } from '@/components/ui/button'
import { useBankId } from '@/integrations/bankid/useBankId'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/login/bankid')({
  component: LoginBankID,
})

function LoginBankID() {
  const bankId = useBankId()
  const [isMobileShowingQR, setIsMobileShowingQR] = useState(false)
  const { t } = useTranslation()

  useEffect(() => {
    bankId.startAuthSession()

    return () => bankId.cancelAuthSession()
  }, [])

  if (bankId.isError) {
    return <BankIDError onRetry={bankId.startAuthSession} rfaCode={bankId.rfaCode} />
  }

  return (
    <AuthSplitLayout>
      <div className="flex-1 flex flex-col items-center justify-center w-full md:flex-none md:mb-10 space-y-10">
        <div className="flex flex-col gap-y-3 items-center">
          <h1 className="text-2xl font-bold md:text-3xl text-center">
            {t('bankIdLoginPage.title')}
          </h1>
          <p className="text-text-secondary text-center max-w-[70vw]">
            <span className={cn('hidden md:block', isMobileShowingQR && 'block')}>
              {t('bankIdLoginPage.scanQrInstruction')}
            </span>
            <span className={cn('md:hidden', isMobileShowingQR && 'hidden')}>
              {t('bankIdLoginPage.openAppInstruction')}
            </span>
          </p>
        </div>

        <div className="space-y-3">
          <div
            className={cn(
              'bg-background size-[280px] max-w-full max-h-full aspect-square p-3 rounded-md',
              /* Make sure border progress animates even when QR code is hidden on mobile due to <ProgressBorder> limitations */
              'fixed opacity-0 pointer-events-none md:relative md:block md:opacity-100',
              isMobileShowingQR && 'relative opacity-100',
            )}
          >
            <ProgressBorder
              className={cn(
                'text-neutral-600 dark:text-eutral-600 p-3 size-full',
                bankId.pollDurationPercentage < 30 && 'text-yellow-500',
                bankId.pollDurationPercentage < 15 && 'text-red-500',
                bankId.status === 'complete' && 'text-green-500',
              )}
              progress={bankId.status === 'complete' ? 100 : bankId.pollDurationPercentage}
            >
              {bankId.qrCode ? (
                <QRCode className="size-full" value={bankId.qrCode} />
              ) : (
                <div className="size-full rounded-md bg-neutral-200 dark:bg-neutral-800" />
              )}
            </ProgressBorder>
          </div>
        </div>
      </div>

      <div className="flex flex-col space-y-2 w-full">
        {/* Desktop */}
        <div className="hidden md:block">
          <Button
            className="w-full"
            disabled={!bankId.orderRef}
            onClick={() => bankId.launchApp()}
            size="lg"
            variant="outline"
          >
            {t('bankIdLoginPage.openOnThisDeviceButton')}
          </Button>
        </div>

        {/* Mobile */}
        <div className="md:hidden flex flex-col space-y-2">
          <Button
            className="w-full"
            onClick={() => setIsMobileShowingQR(!isMobileShowingQR)}
            size="lg"
            variant="outline"
          >
            {isMobileShowingQR
              ? t('bankIdLoginPage.openOnThisDeviceButton')
              : t('bankIdLoginPage.openOnOtherDeviceButton')}
          </Button>
          <Button
            className={cn('w-full', isMobileShowingQR && 'hidden')}
            disabled={!bankId.orderRef}
            onClick={() => bankId.launchApp()}
            size="lg"
          >
            <BankIDLogo className="!size-8" />
            {t('bankIdLoginPage.openOnThisDeviceButton')}
          </Button>
        </div>

        <Link to="/login">
          <Button className="w-full" size="lg" variant="ghost">
            {t('global.back')}
          </Button>
        </Link>
      </div>
    </AuthSplitLayout>
  )
}

const BankIDError = ({
  onRetry,
  rfaCode = RfaCode.RFA22,
}: {
  onRetry(): void
  rfaCode?: RfaCode
}) => {
  const { t } = useTranslation()

  return (
    <AuthSplitLayout>
      <div className="flex-1 flex flex-col items-center justify-center w-full md:flex-none md:mb-6 space-y-6">
        <div className="flex flex-col gap-y-4 items-center">
          <div className="size-[56px] rounded-full bg-red-200 flex items-center justify-center">
            <XIcon className="text-red-600" size={32} />
          </div>
          <div className="flex flex-col gap-y-3">
            <h1 className="text-xl font-bold text-center">
              {t('bankIdLoginPage.loginFailedTitle')}
            </h1>
            <p className="text-muted-foreground text-sm text-center">
              {t(`bankId.status.rfaCode`, { context: rfaCode })}
            </p>
          </div>
        </div>
      </div>

      <div className="flex flex-col space-y-2 w-full">
        <Link to="/login">
          <Button className="w-full" size="lg" variant="outline">
            {t('global.cancel')}
          </Button>
        </Link>

        <Button className="w-full" onClick={onRetry} size="lg">
          {t('global.tryAgain')}
        </Button>
      </div>
    </AuthSplitLayout>
  )
}
